import type { IconName } from "@similarweb/icons";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { QueryBarItem } from "app/components/compare/QueryBarItem";

import type { ICategoryComparison } from "../../categories-context";
import { CategorySearchContainer } from "../styles";
import type { ICategoryModalProps } from "../types";

import { IndustryAnalysisQueryBarItem } from "./IndustryAnalysisQueryBarItem";
import { IndustryAnalysisSearch } from "./IndustryAnalysisSearch";

interface ICategoryQueryBarItemProps {
    category: ICategoryComparison;
    activeCategoryId: string | null;
    showOnlyCategories?: boolean;
    showOnlyCustom?: boolean;
    placeholder?: string;
    categoryModalProps?: ICategoryModalProps;
    isPartnerPage?: boolean;
    isCompare?: boolean;
    customResolveItemIcon?: (item: ICategory, isChild: boolean) => IconName;
    onCategoryClick: (categoryId: string) => void;
    onCategoryRemove: (categoryId: string) => void;
    onCategoryReplace: (oldId: string, newCategory: ICategory) => void;
    onSearchClose: () => void;
    disabledCategories?: (ICategory | ICustomCategory)[];
    hasSelectedCategoryWithSegments?: boolean;
}

export const CategoryQueryBarItem = ({
    category,
    activeCategoryId,
    showOnlyCategories,
    showOnlyCustom,
    placeholder,
    categoryModalProps,
    isPartnerPage,
    isCompare,
    customResolveItemIcon,
    onCategoryClick,
    onCategoryRemove,
    onCategoryReplace,
    onSearchClose,
    disabledCategories = [],
    hasSelectedCategoryWithSegments,
}: ICategoryQueryBarItemProps) => {
    // Use forUrl for URL consistency - this is what's actually in the URL parameters
    const categoryId = category.category.forUrl || category.category.categoryId || category.id;

    const categoryItemComponent = (
        <IndustryAnalysisQueryBarItem
            category={category}
            onItemClick={() => onCategoryClick(categoryId)}
            onItemRemove={() => onCategoryRemove(categoryId)}
            isCompare={isCompare}
        />
    );

    const handleItemClick = (newCategory: ICategory) => {
        onCategoryReplace(categoryId, newCategory);
        onSearchClose();
    };

    const categorySearchComponent = (
        <CategorySearchContainer>
            <IndustryAnalysisSearch
                onItemClick={(newCategory) => handleItemClick(newCategory)}
                onClose={onSearchClose}
                showOnlyCategories={showOnlyCategories}
                showOnlyCustom={showOnlyCustom}
                placeholder={placeholder}
                categoryModalProps={categoryModalProps}
                isPartnerPage={isPartnerPage}
                customResolveItemIcon={customResolveItemIcon}
                selectedCategory={category.category}
                disabledCategories={disabledCategories}
                isCompare={isCompare}
                hasSelectedCategoryWithSegments={hasSelectedCategoryWithSegments}
            />
        </CategorySearchContainer>
    );

    return (
        <QueryBarItem
            key={categoryId}
            id={categoryId}
            isLoading={false}
            activeItem={activeCategoryId === categoryId ? categoryId : null}
            renderComponent={categoryItemComponent}
            searchComponent={categorySearchComponent}
        />
    );
};
